'use client'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Box,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle'
import { StyledAccordionGroup } from './accordionGroup.styles'
import { Button } from '../button/button'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

const getIcon = (expanded) =>
  expanded ? <RemoveCircleIcon /> : <AddCircleIcon />

export const AccordionGroup = ({
  children,
  label,
  items = [],
  accordionProps,
  expandAll = true,
  defaultExpandAll = false,
  wrapperProps,
  hideHeaderOnOpen,
  ...props
}) => {
  const [activeIds, setActiveIds] = useState(() =>
    defaultExpandAll ? items.map(({ id }) => id) : []
  )

  // Tracks every ID this instance has ever seen.
  // Lets us distinguish a user-collapsed item (in seenIdsRef, not in activeIds)
  // from a brand-new dynamic item (not in seenIdsRef) that should auto-expand.
  const seenIdsRef = useRef(null)

  useEffect(() => {
    if (!defaultExpandAll) return

    const allIds = items.map(({ id }) => id)

    if (seenIdsRef.current === null) {
      // First run — useState initializer already expanded everything.
      // Record current IDs and bail; don't call setActiveIds.
      seenIdsRef.current = new Set(allIds)
      return
    }

    // Only auto-expand IDs we have never seen before (truly new dynamic items).
    // IDs the user manually collapsed are already in seenIdsRef, so they stay collapsed.
    const trulyNewIds = allIds.filter(id => !seenIdsRef.current.has(id))
    allIds.forEach(id => seenIdsRef.current.add(id))

    if (trulyNewIds.length > 0) {
      setActiveIds(prev => [...prev, ...trulyNewIds])
    }
  }, [items, defaultExpandAll])

  const onGroupToggle = useCallback(() => {
    setActiveIds((ids) =>
      ids.length === items.length ? [] : items.map(({ id }) => id)
    )
  }, [items])

  const onAccordionChange = useCallback(
    (id) => () => {
      setActiveIds((ids) =>
        ids.includes(id)
          ? ids.filter((activeId) => activeId !== id)
          : [...ids, id]
      )
    },
    []
  )

  const allExpanded = useMemo(
    () => activeIds.length === items.length,
    [activeIds, items]
  )

  const mergedSlotProps = useMemo(
    () => ({
      ...(accordionProps?.slotProps || {}),
      transition: {
        mountOnEnter: true,
        unmountOnExit: true,
        ...(accordionProps?.slotProps?.transition || {}),
      },
    }),
    [accordionProps?.slotProps]
  )

  return (
    <StyledAccordionGroup {...props}>
      <Stack
        spacing={2}
        justifyContent="end"
        alignItems="center"
        direction='row'
        className="group-header"
		data-tracking="body-accordion-main"
      >
        {label && <>{label}</>}
        {expandAll && (
          <Button
            color="tertiary"
            pr={0}
            icon={allExpanded ? RemoveIcon : AddIcon}
            onClick={onGroupToggle}
			data-tracking="body-accordion-expand"
          >
            {allExpanded ? 'Collapse' : 'Expand'} All
          </Button>
        )}
      </Stack>
      <Box className="accordion-wrapper" {...wrapperProps}>
        {items.map(({ id, header, body, ...item }) => {
          const expanded = activeIds.includes(id)
          return (
            <Accordion
              disableGutters
              elevation={0}
              key={id}
              onChange={onAccordionChange(id)}
			  data-tracking="body-accordion-top"
              slotProps={mergedSlotProps}
              {...accordionProps}
              {...item}
              expanded={expanded}
            >
              <AccordionSummary
				  expandIcon={getIcon(expanded)}
				  data-tracking="body-accordion-summary-item"
				  sx={{
					'& .MuiAccordionSummary-expandIconWrapper': {
					  ml: { xs: 1.25, sm: 0 }, 
					},
				  }}
				>
				  {!(expanded && hideHeaderOnOpen) && header}
				</AccordionSummary>
              <AccordionDetails>{body}</AccordionDetails>
            </Accordion>
          )
        })}

        {children}
      </Box>
    </StyledAccordionGroup>
  )
}
